const LOADER_CLASS = 'stripe-express-checkout-mount-loader';
const LOADER_VISIBLE_CLASS = 'stripe-express-checkout-mount-loader--visible';
const MOUNT_HOST_CLASS = 'stripe-express-checkout-mount-host';

/**
 * Hide the mount loader when Stripe never fires `ready` / `loaderror` (e.g. hung CDN).
 * Primary UX is those events; this is a safety net so the skeleton does not persist forever.
 */
export const MOUNT_LOADER_FALLBACK_MS = 10000;

/**
 * @param {Element} mountElement
 * @returns {Element | null}
 */
function getMountHost(mountElement) {
	const parent = mountElement.parentElement;
	if (!parent) {
		return null;
	}
	if (parent.classList.contains(MOUNT_HOST_CLASS)) {
		return parent;
	}
	if (parent.childElementCount === 1) {
		parent.classList.add(MOUNT_HOST_CLASS);
		return parent;
	}
	const host = document.createElement('div');
	host.className = MOUNT_HOST_CLASS;
	mountElement.before(host);
	host.appendChild(mountElement);
	return host;
}

/**
 * Stripe ECE requires an empty mount node. Strip legacy loader nodes and any other children.
 *
 * @param {Element} mountElement
 * @returns {void}
 */
export function prepareMountElementForStripe(mountElement) {
	if (mountElement?.nodeType !== 1) {
		return;
	}
	mountElement.querySelectorAll(`.${LOADER_CLASS}`).forEach((node) => {
		node.remove();
	});
	if (mountElement.childNodes.length > 0) {
		mountElement.textContent = '';
	}
}

/**
 * Shows stacked skeleton bars over the mount slot while Stripe.js and ECE initialize.
 *
 * @param {Element} mountElement
 * @returns {void}
 */
export function showMountLoader(mountElement) {
	if (mountElement?.nodeType !== 1) {
		return;
	}

	const host = getMountHost(mountElement);
	if (!host) {
		return;
	}

	let loader = host.querySelector(`.${LOADER_CLASS}`);
	if (!loader) {
		loader = document.createElement('div');
		loader.className = LOADER_CLASS;
		loader.setAttribute('aria-live', 'polite');
		loader.innerHTML = `
			<div class="stripe-express-checkout-mount-loader__skeleton" aria-hidden="true">
				<span class="stripe-express-checkout-mount-loader__bar"></span>
				<span class="stripe-express-checkout-mount-loader__bar"></span>
				<span class="stripe-express-checkout-mount-loader__bar"></span>
			</div>
			<span class="sr-only">Loading express checkout options</span>
		`;
		mountElement.before(loader);
	}

	mountElement.setAttribute('aria-busy', 'true');
	loader.classList.add(LOADER_VISIBLE_CLASS);
}

/**
 * @param {Element} mountElement
 * @returns {void}
 */
export function hideMountLoader(mountElement) {
	if (mountElement?.nodeType !== 1) {
		return;
	}

	mountElement.removeAttribute('aria-busy');
	const host = mountElement.parentElement;
	if (!host) {
		return;
	}
	const loader = host.querySelector(`.${LOADER_CLASS}`);
	if (loader) {
		loader.classList.remove(LOADER_VISIBLE_CLASS);
	}
}
